/** Audience filter conditions for campaign recipient selection (extensible). */

import type { BookingExcludePeriod, BookingExcludePreset } from './campaignBookingExclude';
import { formatBookingExcludePeriodLabel } from './campaignBookingExclude';

export type AudienceConditionKind = 'tags' | 'booking_exclude';

export const AUDIENCE_CONDITION_OPTIONS: { id: AudienceConditionKind; label: string }[] = [
  { id: 'tags', label: 'Tags' },
  { id: 'booking_exclude', label: 'Has booking in period' },
];

export interface TagsAudienceCondition {
  kind: 'tags';
  type: 'includes' | 'excludes';
  tagIds: string[];
  tagNames: string[];
}

export interface BookingExcludeAudienceCondition {
  kind: 'booking_exclude';
  preset: BookingExcludePreset;
  startDate: string;
  endDate: string;
  /** Human-readable period for chips in the UI */
  label: string;
}

export type AudienceCondition = TagsAudienceCondition | BookingExcludeAudienceCondition;

export type AddConditionStep = null | 'choose-type' | AudienceConditionKind;

export function getTagFiltersFromConditions(conditions: AudienceCondition[]): {
  includeTags: string[];
  excludeTags: string[];
} {
  const includeTags: string[] = [];
  const excludeTags: string[] = [];

  for (const condition of conditions) {
    if (condition.kind !== 'tags') continue;
    if (condition.type === 'includes') {
      includeTags.push(...condition.tagNames);
    } else {
      excludeTags.push(...condition.tagNames);
    }
  }

  return { includeTags, excludeTags };
}

export function getBookingExcludePeriodsFromConditions(
  conditions: AudienceCondition[]
): BookingExcludePeriod[] {
  return conditions
    .filter((c): c is BookingExcludeAudienceCondition => c.kind === 'booking_exclude')
    .map((c) => ({ start: c.startDate, end: c.endDate }))
    .filter((p) => p.start && p.end);
}

export function hasTagConditions(conditions: AudienceCondition[]): boolean {
  return conditions.some((c) => c.kind === 'tags');
}

export function hasBookingExcludeConditions(conditions: AudienceCondition[]): boolean {
  return conditions.some((c) => c.kind === 'booking_exclude');
}

export function audienceConditionKindLabel(kind: AudienceConditionKind): string {
  if (kind === 'booking_exclude') return 'Booking';
  return AUDIENCE_CONDITION_OPTIONS.find((o) => o.id === kind)?.label ?? kind;
}

export function bookingExcludeConditionSummary(condition: BookingExcludeAudienceCondition): string {
  return `Exclude customers with a booking ${condition.label}`;
}
